import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import emailjs from '@emailjs/browser';
import { sendBookingStatusEmail } from '../../utils/emailService';
import UserShell from '../Layout/UserShell';
import ShipmentTabs from '../common/ShipmentTabs';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Package,
  Weight,
  Ruler,
  FileText,
  Upload,
  Plus,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Send,
  Trash2,
  Eye,
  Plane,
  Ship,
  Clock,
  Tag,
  Loader2,
  RefreshCw,
  History,
  PlusCircle,
  Pencil,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiCall, API_ENDPOINTS } from '../../config/api';
import { getCloudFrontUrl } from '../../utils/s3Upload';

// ─── Constants ───
const SERVICE_OPTIONS = [
  'All',
  'Customs Clearance',
  'Freight',
  'Warehousing',
  'Transportation',
  'Destination Clearance',
  'International Courier',
  'Door to Door',
  'Port to Port',
];

const INCOTERM_OPTIONS = [
  'FOB', 'CIF', 'C&F', 'CIP', 'EXW', 'FCA', 'CPT',
  'DPU', 'DAP', 'DDP', 'FAS', 'CFR',
];

const SHIPMENT_TYPE_OPTIONS = ['FCL', 'LCL'];

const CONTAINER_TYPE_OPTIONS = [
  '20GP', '40GP', '40HQ', '45HQ',
  'Reefer Container', 'Open Top Container', 'Flat Rack Container',
  'Tank', 'Hanger Container', 'Other',
];

// ─── Indian Air Ports ───
const INDIAN_AIR_PORTS = [
  'Delhi (DEL)', 'Mumbai (BOM)', 'Chennai (MAA)', 'Bangalore (BLR)', 'Hyderabad (HYD)',
  'Kolkata (CCU)', 'Ahmedabad (AMD)', 'Cochin (COK)', 'Goa (GOI)', 'Jaipur (JAI)',
  'Lucknow (LKO)', 'Amritsar (ATQ)', 'Coimbatore (CJB)', 'Thiruvananthapuram (TRV)',
  'Guwahati (GAU)', 'Mangalore (IXE)', 'Nagpur (NAG)', 'Pune (PNQ)', 'Varanasi (VNS)',
  'Indore (IDR)', 'Bhopal (BHO)', 'Patna (PAT)', 'Ranchi (IXR)', 'Visakhapatnam (VTZ)',
  'Madurai (IXM)', 'Tiruchirappalli (TRZ)', 'Srinagar (SXR)', 'Chandigarh (IXC)',
];

// ─── Indian Sea Ports ───
const INDIAN_SEA_PORTS = [
  'Mundra (INMUN)', 'JNPT / Nhava Sheva (INNSA)', 'Chennai (INMAA)', 'Kolkata (INCCU)',
  'Visakhapatnam (INVTZ)', 'Cochin (INCOK)', 'Pipavav (INPAV)', 'Kandla (INIXY)',
  'Tuticorin (INTUT)', 'Mangalore (INIXE)', 'Goa / Mormugao (INMRM)', 'Haldia (INHAL)',
  'Kakinada (INKAK)', 'Krishnapatnam (INKRI)', 'Ennore (INENO)', 'Paradip (INPRT)',
  'Hazira (INHZA)', 'Dahej (INDAH)', 'Gangavaram (INGVR)',
];

// ─── Countries (for Origin Country in Import / Destination Country in Export) ───
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bermuda', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
  'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'DR Congo', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'Indonesia',
  'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon',
  'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macau', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
  'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
  'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Caledonia', 'New Zealand', 'Nicaragua',
  'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
  'Romania', 'Russia', 'Rwanda', 'Samoa', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
  'Syria', 'São Tomé and Príncipe', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste',
  'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'UAE',
  'UK', 'USA', 'Uganda', 'Ukraine', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe',
];

// Country code to flag emoji mapping
const countryToFlag = (countryName) => {
  const countryFlags = {
    'afghanistan': '🇦🇫', 'albania': '🇦🇱', 'algeria': '🇩🇿', 'andorra': '🇦🇩', 'angola': '🇦🇴',
    'antigua and barbuda': '🇦🇬', 'argentina': '🇦🇷', 'armenia': '🇦🇲', 'australia': '🇦🇺', 'austria': '🇦🇹',
    'azerbaijan': '🇦🇿', 'bahamas': '🇧🇸', 'bahrain': '🇧🇭', 'bangladesh': '🇧🇩', 'barbados': '🇧🇧',
    'belarus': '🇧🇾', 'belgium': '🇧🇪', 'belize': '🇧🇿', 'benin': '🇧🇯', 'bermuda': '🇧🇲',
    'bhutan': '🇧🇹', 'bolivia': '🇧🇴', 'bosnia and herzegovina': '🇧🇦', 'botswana': '🇧🇼', 'brazil': '🇧🇷',
    'brunei': '🇧🇳', 'bulgaria': '🇧🇬', 'burkina faso': '🇧🇫', 'burundi': '🇧🇮', 'cambodia': '🇰🇭',
    'cameroon': '🇨🇲', 'canada': '🇨🇦', 'cape verde': '🇨🇻', 'central african republic': '🇨🇫', 'chad': '🇹🇩',
    'chile': '🇨🇱', 'china': '🇨🇳', 'colombia': '🇨🇴', 'comoros': '🇰🇲', 'congo': '🇨🇬',
    'costa rica': '🇨🇷', 'croatia': '🇭🇷', 'cuba': '🇨🇺', 'cyprus': '🇨🇾', 'czech republic': '🇨🇿',
    'dr congo': '🇨🇩', 'denmark': '🇩🇰', 'djibouti': '🇩🇯', 'dominica': '🇩🇲', 'dominican republic': '🇩🇴',
    'ecuador': '🇪🇨', 'egypt': '🇪🇬', 'el salvador': '🇸🇻', 'equatorial guinea': '🇬🇶', 'eritrea': '🇪🇷',
    'eswatini': '🇸🇿', 'estonia': '🇪🇪', 'ethiopia': '🇪🇹', 'fiji': '🇫🇯', 'finland': '🇫🇮',
    'france': '🇫🇷', 'gabon': '🇬🇦', 'gambia': '🇬🇲', 'georgia': '🇬🇪', 'germany': '🇩🇪',
    'ghana': '🇬🇭', 'greece': '🇬🇷', 'grenada': '🇬🇩', 'guatemala': '🇬🇹', 'guinea': '🇬🇳',
    'guinea-bissau': '🇬🇼', 'guyana': '🇬🇾', 'haiti': '🇭🇹', 'honduras': '🇭🇳', 'hong kong': '🇭🇰',
    'hungary': '🇭🇺', 'iceland': '🇮🇸', 'india': '🇮🇳', 'indonesia': '🇮🇩', 'iran': '🇮🇷',
    'iraq': '🇮🇶', 'ireland': '🇮🇪', 'israel': '🇮🇱', 'italy': '🇮🇹', 'ivory coast': '🇨🇮',
    'jamaica': '🇯🇲', 'japan': '🇯🇵', 'jordan': '🇯🇴', 'kazakhstan': '🇰🇿', 'kenya': '🇰🇪',
    'kiribati': '🇰🇮', 'kosovo': '🇽🇰', 'kuwait': '🇰🇼', 'kyrgyzstan': '🇰🇬', 'laos': '🇱🇦',
    'latvia': '🇱🇻', 'lebanon': '🇱🇧', 'lesotho': '🇱🇸', 'liberia': '🇱🇷', 'libya': '🇱🇾',
    'liechtenstein': '🇱🇮', 'lithuania': '🇱🇹', 'luxembourg': '🇱🇺', 'macau': '🇲🇴', 'madagascar': '🇲🇬',
    'malawi': '🇲🇼', 'malaysia': '🇲🇾', 'maldives': '🇲🇻', 'mali': '🇲🇱', 'malta': '🇲🇹',
    'marshall islands': '🇲🇭', 'mauritania': '🇲🇷', 'mauritius': '🇲🇺', 'mexico': '🇲🇽', 'micronesia': '🇫🇲',
    'moldova': '🇲🇩', 'monaco': '🇲🇨', 'mongolia': '🇲🇳', 'montenegro': '🇲🇪', 'morocco': '🇲🇦',
    'mozambique': '🇲🇿', 'myanmar': '🇲🇲', 'namibia': '🇳🇦', 'nauru': '🇳🇷', 'nepal': '🇳🇵',
    'netherlands': '🇳🇱', 'new caledonia': '🇳🇨', 'new zealand': '🇳🇿', 'nicaragua': '🇳🇮', 'niger': '🇳🇪',
    'nigeria': '🇳🇬', 'north korea': '🇰🇵', 'north macedonia': '🇲🇰', 'norway': '🇳🇴', 'oman': '🇴🇲',
    'pakistan': '🇵🇰', 'palau': '🇵🇼', 'panama': '🇵🇦', 'papua new guinea': '🇵🇬', 'paraguay': '🇵🇾',
    'peru': '🇵🇪', 'philippines': '🇵🇭', 'poland': '🇵🇱', 'portugal': '🇵🇹', 'qatar': '🇶🇦',
    'romania': '🇷🇴', 'russia': '🇷🇺', 'rwanda': '🇷🇼', 'samoa': '🇼🇸', 'saudi arabia': '🇸🇦',
    'senegal': '🇸🇳', 'serbia': '🇷🇸', 'seychelles': '🇸🇨', 'sierra leone': '🇸🇱', 'singapore': '🇸🇬',
    'slovakia': '🇸🇰', 'slovenia': '🇸🇮', 'solomon islands': '🇸🇧', 'somalia': '🇸🇴', 'south africa': '🇿🇦',
    'south korea': '🇰🇷', 'south sudan': '🇸🇸', 'spain': '🇪🇸', 'sri lanka': '🇱🇰', 'sudan': '🇸🇩',
    'suriname': '🇸🇷', 'sweden': '🇸🇪', 'switzerland': '🇨🇭', 'syria': '🇸🇾', 'são tomé and príncipe': '🇸🇹',
    'taiwan': '🇹🇼', 'tajikistan': '🇹🇯', 'tanzania': '🇹🇿', 'thailand': '🇹🇭', 'timor-leste': '🇹🇱',
    'togo': '🇹🇬', 'tonga': '🇹🇴', 'trinidad and tobago': '🇹🇹', 'tunisia': '🇹🇳', 'turkey': '🇹🇷',
    'turkmenistan': '🇹🇲', 'tuvalu': '🇹🇻', 'uae': '🇦🇪', 'united arab emirates': '🇦🇪', 'uk': '🇬🇧',
    'united kingdom': '🇬🇧', 'usa': '🇺🇸', 'united states': '🇺🇸', 'uganda': '🇺🇬', 'ukraine': '🇺🇦',
    'uruguay': '🇺🇾', 'uzbekistan': '🇺🇿', 'vanuatu': '🇻🇺', 'venezuela': '🇻🇪', 'vietnam': '🇻🇳',
    'yemen': '🇾🇪', 'zambia': '🇿🇲', 'zimbabwe': '🇿🇼',
  };
  if (!countryName) return '🌍';
  const key = countryName.toLowerCase().trim();
  return countryFlags[key] || '🌍';
};

// ─── International Cities — ALL Countries ───
const INTERNATIONAL_CITIES = [
  // ── Africa ──
  { city: 'Algiers', country: 'Algeria' }, { city: 'Oran', country: 'Algeria' }, { city: 'Constantine', country: 'Algeria' },
  { city: 'Luanda', country: 'Angola' }, { city: 'Lobito', country: 'Angola' },
  { city: 'Cotonou', country: 'Benin' }, { city: 'Porto-Novo', country: 'Benin' },
  { city: 'Gaborone', country: 'Botswana' }, { city: 'Francistown', country: 'Botswana' },
  { city: 'Ouagadougou', country: 'Burkina Faso' },
  { city: 'Bujumbura', country: 'Burundi' },
  { city: 'Douala', country: 'Cameroon' }, { city: 'Yaoundé', country: 'Cameroon' },
  { city: 'Praia', country: 'Cape Verde' },
  { city: 'Bangui', country: 'Central African Republic' },
  { city: "N'Djamena", country: 'Chad' },
  { city: 'Moroni', country: 'Comoros' },
  { city: 'Brazzaville', country: 'Congo' }, { city: 'Pointe-Noire', country: 'Congo' },
  { city: 'Kinshasa', country: 'DR Congo' }, { city: 'Lubumbashi', country: 'DR Congo' }, { city: 'Matadi', country: 'DR Congo' },
  { city: 'Abidjan', country: 'Ivory Coast' }, { city: 'San Pedro', country: 'Ivory Coast' },
  { city: 'Djibouti City', country: 'Djibouti' },
  { city: 'Cairo', country: 'Egypt' }, { city: 'Alexandria', country: 'Egypt' }, { city: 'Port Said', country: 'Egypt' }, { city: 'Suez', country: 'Egypt' },
  { city: 'Malabo', country: 'Equatorial Guinea' },
  { city: 'Asmara', country: 'Eritrea' }, { city: 'Massawa', country: 'Eritrea' },
  { city: 'Mbabane', country: 'Eswatini' },
  { city: 'Addis Ababa', country: 'Ethiopia' }, { city: 'Dire Dawa', country: 'Ethiopia' },
  { city: 'Libreville', country: 'Gabon' }, { city: 'Port-Gentil', country: 'Gabon' },
  { city: 'Banjul', country: 'Gambia' },
  { city: 'Accra', country: 'Ghana' }, { city: 'Tema', country: 'Ghana' }, { city: 'Kumasi', country: 'Ghana' }, { city: 'Takoradi', country: 'Ghana' },
  { city: 'Conakry', country: 'Guinea' },
  { city: 'Bissau', country: 'Guinea-Bissau' },
  { city: 'Nairobi', country: 'Kenya' }, { city: 'Mombasa', country: 'Kenya' }, { city: 'Kisumu', country: 'Kenya' },
  { city: 'Maseru', country: 'Lesotho' },
  { city: 'Monrovia', country: 'Liberia' },
  { city: 'Tripoli', country: 'Libya' }, { city: 'Benghazi', country: 'Libya' }, { city: 'Misrata', country: 'Libya' },
  { city: 'Antananarivo', country: 'Madagascar' }, { city: 'Toamasina', country: 'Madagascar' },
  { city: 'Lilongwe', country: 'Malawi' }, { city: 'Blantyre', country: 'Malawi' },
  { city: 'Bamako', country: 'Mali' },
  { city: 'Nouakchott', country: 'Mauritania' },
  { city: 'Port Louis', country: 'Mauritius' },
  { city: 'Casablanca', country: 'Morocco' }, { city: 'Tangier', country: 'Morocco' }, { city: 'Rabat', country: 'Morocco' }, { city: 'Marrakech', country: 'Morocco' },
  { city: 'Maputo', country: 'Mozambique' }, { city: 'Beira', country: 'Mozambique' }, { city: 'Nacala', country: 'Mozambique' },
  { city: 'Windhoek', country: 'Namibia' }, { city: 'Walvis Bay', country: 'Namibia' },
  { city: 'Niamey', country: 'Niger' },
  { city: 'Lagos', country: 'Nigeria' }, { city: 'Abuja', country: 'Nigeria' }, { city: 'Port Harcourt', country: 'Nigeria' }, { city: 'Kano', country: 'Nigeria' }, { city: 'Apapa', country: 'Nigeria' }, { city: 'Tin Can Island', country: 'Nigeria' },
  { city: 'Kigali', country: 'Rwanda' },
  { city: 'São Tomé', country: 'São Tomé and Príncipe' },
  { city: 'Dakar', country: 'Senegal' },
  { city: 'Victoria', country: 'Seychelles' },
  { city: 'Freetown', country: 'Sierra Leone' },
  { city: 'Mogadishu', country: 'Somalia' }, { city: 'Berbera', country: 'Somalia' },
  { city: 'Johannesburg', country: 'South Africa' }, { city: 'Cape Town', country: 'South Africa' }, { city: 'Durban', country: 'South Africa' }, { city: 'Pretoria', country: 'South Africa' }, { city: 'Port Elizabeth', country: 'South Africa' },
  { city: 'Juba', country: 'South Sudan' },
  { city: 'Khartoum', country: 'Sudan' }, { city: 'Port Sudan', country: 'Sudan' },
  { city: 'Dar es Salaam', country: 'Tanzania' }, { city: 'Zanzibar', country: 'Tanzania' }, { city: 'Dodoma', country: 'Tanzania' },
  { city: 'Lomé', country: 'Togo' },
  { city: 'Tunis', country: 'Tunisia' }, { city: 'Sfax', country: 'Tunisia' },
  { city: 'Kampala', country: 'Uganda' },
  { city: 'Lusaka', country: 'Zambia' }, { city: 'Ndola', country: 'Zambia' },
  { city: 'Harare', country: 'Zimbabwe' }, { city: 'Bulawayo', country: 'Zimbabwe' },

  // ── Asia ──
  { city: 'Kabul', country: 'Afghanistan' }, { city: 'Mazar-i-Sharif', country: 'Afghanistan' },
  { city: 'Yerevan', country: 'Armenia' },
  { city: 'Baku', country: 'Azerbaijan' },
  { city: 'Manama', country: 'Bahrain' },
  { city: 'Dhaka', country: 'Bangladesh' }, { city: 'Chittagong', country: 'Bangladesh' }, { city: 'Khulna', country: 'Bangladesh' },
  { city: 'Thimphu', country: 'Bhutan' }, { city: 'Phuentsholing', country: 'Bhutan' },
  { city: 'Bandar Seri Begawan', country: 'Brunei' },
  { city: 'Phnom Penh', country: 'Cambodia' }, { city: 'Sihanoukville', country: 'Cambodia' },
  { city: 'Beijing', country: 'China' }, { city: 'Shanghai', country: 'China' }, { city: 'Guangzhou', country: 'China' }, { city: 'Shenzhen', country: 'China' }, { city: 'Tianjin', country: 'China' }, { city: 'Chengdu', country: 'China' }, { city: 'Chongqing', country: 'China' }, { city: 'Dalian', country: 'China' }, { city: 'Hangzhou', country: 'China' }, { city: 'Nanjing', country: 'China' }, { city: 'Ningbo', country: 'China' }, { city: 'Qingdao', country: 'China' }, { city: 'Suzhou', country: 'China' }, { city: 'Wuhan', country: 'China' }, { city: 'Xiamen', country: 'China' }, { city: 'Xian', country: 'China' }, { city: 'Yiwu', country: 'China' }, { city: 'Zhengzhou', country: 'China' }, { city: 'Foshan', country: 'China' }, { city: 'Dongguan', country: 'China' },
  { city: 'Tbilisi', country: 'Georgia' }, { city: 'Batumi', country: 'Georgia' },
  { city: 'Hong Kong', country: 'Hong Kong' }, { city: 'Kwai Chung', country: 'Hong Kong' },
  { city: 'Jakarta', country: 'Indonesia' }, { city: 'Surabaya', country: 'Indonesia' }, { city: 'Medan', country: 'Indonesia' }, { city: 'Semarang', country: 'Indonesia' }, { city: 'Makassar', country: 'Indonesia' }, { city: 'Bandung', country: 'Indonesia' }, { city: 'Denpasar', country: 'Indonesia' }, { city: 'Tanjung Priok', country: 'Indonesia' },
  { city: 'Tehran', country: 'Iran' }, { city: 'Isfahan', country: 'Iran' }, { city: 'Bandar Abbas', country: 'Iran' }, { city: 'Tabriz', country: 'Iran' }, { city: 'Shiraz', country: 'Iran' },
  { city: 'Baghdad', country: 'Iraq' }, { city: 'Basra', country: 'Iraq' }, { city: 'Erbil', country: 'Iraq' },
  { city: 'Tel Aviv', country: 'Israel' }, { city: 'Haifa', country: 'Israel' }, { city: 'Ashdod', country: 'Israel' },
  { city: 'Tokyo', country: 'Japan' }, { city: 'Osaka', country: 'Japan' }, { city: 'Yokohama', country: 'Japan' }, { city: 'Nagoya', country: 'Japan' }, { city: 'Kobe', country: 'Japan' }, { city: 'Fukuoka', country: 'Japan' }, { city: 'Sapporo', country: 'Japan' }, { city: 'Kyoto', country: 'Japan' },
  { city: 'Amman', country: 'Jordan' }, { city: 'Aqaba', country: 'Jordan' },
  { city: 'Almaty', country: 'Kazakhstan' }, { city: 'Astana', country: 'Kazakhstan' }, { city: 'Aktau', country: 'Kazakhstan' },
  { city: 'Kuwait City', country: 'Kuwait' }, { city: 'Shuwaikh', country: 'Kuwait' },
  { city: 'Bishkek', country: 'Kyrgyzstan' },
  { city: 'Vientiane', country: 'Laos' },
  { city: 'Beirut', country: 'Lebanon' }, { city: 'Tripoli', country: 'Lebanon' },
  { city: 'Macau', country: 'Macau' },
  { city: 'Kuala Lumpur', country: 'Malaysia' }, { city: 'Penang', country: 'Malaysia' }, { city: 'Johor Bahru', country: 'Malaysia' }, { city: 'Port Klang', country: 'Malaysia' }, { city: 'Tanjung Pelepas', country: 'Malaysia' }, { city: 'Kuching', country: 'Malaysia' },
  { city: 'Malé', country: 'Maldives' },
  { city: 'Ulaanbaatar', country: 'Mongolia' },
  { city: 'Yangon', country: 'Myanmar' }, { city: 'Mandalay', country: 'Myanmar' },
  { city: 'Kathmandu', country: 'Nepal' }, { city: 'Biratnagar', country: 'Nepal' }, { city: 'Birgunj', country: 'Nepal' },
  { city: 'Pyongyang', country: 'North Korea' },
  { city: 'Muscat', country: 'Oman' }, { city: 'Salalah', country: 'Oman' }, { city: 'Sohar', country: 'Oman' },
  { city: 'Karachi', country: 'Pakistan' }, { city: 'Lahore', country: 'Pakistan' }, { city: 'Islamabad', country: 'Pakistan' }, { city: 'Faisalabad', country: 'Pakistan' }, { city: 'Peshawar', country: 'Pakistan' }, { city: 'Sialkot', country: 'Pakistan' }, { city: 'Gwadar', country: 'Pakistan' },
  { city: 'Manila', country: 'Philippines' }, { city: 'Cebu', country: 'Philippines' }, { city: 'Davao', country: 'Philippines' }, { city: 'Subic Bay', country: 'Philippines' },
  { city: 'Doha', country: 'Qatar' }, { city: 'Hamad Port', country: 'Qatar' },
  { city: 'Riyadh', country: 'Saudi Arabia' }, { city: 'Jeddah', country: 'Saudi Arabia' }, { city: 'Dammam', country: 'Saudi Arabia' }, { city: 'Jubail', country: 'Saudi Arabia' }, { city: 'Yanbu', country: 'Saudi Arabia' }, { city: 'Mecca', country: 'Saudi Arabia' }, { city: 'Medina', country: 'Saudi Arabia' },
  { city: 'Singapore', country: 'Singapore' }, { city: 'Jurong', country: 'Singapore' },
  { city: 'Seoul', country: 'South Korea' }, { city: 'Busan', country: 'South Korea' }, { city: 'Incheon', country: 'South Korea' }, { city: 'Gwangyang', country: 'South Korea' }, { city: 'Ulsan', country: 'South Korea' },
  { city: 'Colombo', country: 'Sri Lanka' }, { city: 'Hambantota', country: 'Sri Lanka' },
  { city: 'Damascus', country: 'Syria' }, { city: 'Latakia', country: 'Syria' },
  { city: 'Taipei', country: 'Taiwan' }, { city: 'Kaohsiung', country: 'Taiwan' }, { city: 'Taichung', country: 'Taiwan' }, { city: 'Keelung', country: 'Taiwan' },
  { city: 'Dushanbe', country: 'Tajikistan' },
  { city: 'Bangkok', country: 'Thailand' }, { city: 'Laem Chabang', country: 'Thailand' }, { city: 'Chiang Mai', country: 'Thailand' }, { city: 'Phuket', country: 'Thailand' }, { city: 'Map Ta Phut', country: 'Thailand' },
  { city: 'Dili', country: 'Timor-Leste' },
  { city: 'Ashgabat', country: 'Turkmenistan' }, { city: 'Turkmenbashi', country: 'Turkmenistan' },
  { city: 'Abu Dhabi', country: 'UAE' }, { city: 'Dubai', country: 'UAE' }, { city: 'Sharjah', country: 'UAE' }, { city: 'Jebel Ali', country: 'UAE' }, { city: 'Ajman', country: 'UAE' }, { city: 'Ras Al Khaimah', country: 'UAE' }, { city: 'Fujairah', country: 'UAE' },
  { city: 'Tashkent', country: 'Uzbekistan' }, { city: 'Bukhara', country: 'Uzbekistan' },
  { city: 'Hanoi', country: 'Vietnam' }, { city: 'Ho Chi Minh City', country: 'Vietnam' }, { city: 'Hai Phong', country: 'Vietnam' }, { city: 'Da Nang', country: 'Vietnam' },
  { city: "Sana'a", country: 'Yemen' }, { city: 'Aden', country: 'Yemen' },

  // ── Europe ──
  { city: 'Tirana', country: 'Albania' }, { city: 'Durrës', country: 'Albania' },
  { city: 'Andorra la Vella', country: 'Andorra' },
  { city: 'Vienna', country: 'Austria' }, { city: 'Graz', country: 'Austria' }, { city: 'Linz', country: 'Austria' },
  { city: 'Minsk', country: 'Belarus' },
  { city: 'Brussels', country: 'Belgium' }, { city: 'Antwerp', country: 'Belgium' }, { city: 'Ghent', country: 'Belgium' }, { city: 'Zeebrugge', country: 'Belgium' },
  { city: 'Sarajevo', country: 'Bosnia and Herzegovina' },
  { city: 'Sofia', country: 'Bulgaria' }, { city: 'Varna', country: 'Bulgaria' }, { city: 'Plovdiv', country: 'Bulgaria' },
  { city: 'Zagreb', country: 'Croatia' }, { city: 'Rijeka', country: 'Croatia' }, { city: 'Split', country: 'Croatia' },
  { city: 'Nicosia', country: 'Cyprus' }, { city: 'Limassol', country: 'Cyprus' },
  { city: 'Prague', country: 'Czech Republic' }, { city: 'Brno', country: 'Czech Republic' },
  { city: 'Copenhagen', country: 'Denmark' }, { city: 'Aarhus', country: 'Denmark' },
  { city: 'Tallinn', country: 'Estonia' },
  { city: 'Helsinki', country: 'Finland' }, { city: 'Turku', country: 'Finland' },
  { city: 'Paris', country: 'France' }, { city: 'Marseille', country: 'France' }, { city: 'Lyon', country: 'France' }, { city: 'Le Havre', country: 'France' }, { city: 'Toulouse', country: 'France' }, { city: 'Nice', country: 'France' }, { city: 'Dunkirk', country: 'France' },
  { city: 'Berlin', country: 'Germany' }, { city: 'Hamburg', country: 'Germany' }, { city: 'Frankfurt', country: 'Germany' }, { city: 'Munich', country: 'Germany' }, { city: 'Bremen', country: 'Germany' }, { city: 'Bremerhaven', country: 'Germany' }, { city: 'Cologne', country: 'Germany' }, { city: 'Düsseldorf', country: 'Germany' }, { city: 'Stuttgart', country: 'Germany' },
  { city: 'Athens', country: 'Greece' }, { city: 'Thessaloniki', country: 'Greece' }, { city: 'Piraeus', country: 'Greece' },
  { city: 'Budapest', country: 'Hungary' }, { city: 'Debrecen', country: 'Hungary' },
  { city: 'Reykjavik', country: 'Iceland' },
  { city: 'Dublin', country: 'Ireland' }, { city: 'Cork', country: 'Ireland' },
  { city: 'Rome', country: 'Italy' }, { city: 'Milan', country: 'Italy' }, { city: 'Naples', country: 'Italy' }, { city: 'Genoa', country: 'Italy' }, { city: 'Venice', country: 'Italy' }, { city: 'Turin', country: 'Italy' }, { city: 'Trieste', country: 'Italy' }, { city: 'La Spezia', country: 'Italy' },
  { city: 'Pristina', country: 'Kosovo' },
  { city: 'Riga', country: 'Latvia' },
  { city: 'Vaduz', country: 'Liechtenstein' },
  { city: 'Vilnius', country: 'Lithuania' }, { city: 'Klaipeda', country: 'Lithuania' },
  { city: 'Luxembourg City', country: 'Luxembourg' },
  { city: 'Skopje', country: 'North Macedonia' },
  { city: 'Valletta', country: 'Malta' }, { city: 'Marsaxlokk', country: 'Malta' },
  { city: 'Chișinău', country: 'Moldova' },
  { city: 'Monaco', country: 'Monaco' },
  { city: 'Podgorica', country: 'Montenegro' }, { city: 'Bar', country: 'Montenegro' },
  { city: 'Amsterdam', country: 'Netherlands' }, { city: 'Rotterdam', country: 'Netherlands' }, { city: 'The Hague', country: 'Netherlands' }, { city: 'Eindhoven', country: 'Netherlands' },
  { city: 'Oslo', country: 'Norway' }, { city: 'Bergen', country: 'Norway' }, { city: 'Stavanger', country: 'Norway' },
  { city: 'Warsaw', country: 'Poland' }, { city: 'Gdansk', country: 'Poland' }, { city: 'Gdynia', country: 'Poland' }, { city: 'Krakow', country: 'Poland' }, { city: 'Wroclaw', country: 'Poland' },
  { city: 'Lisbon', country: 'Portugal' }, { city: 'Porto', country: 'Portugal' }, { city: 'Sines', country: 'Portugal' },
  { city: 'Bucharest', country: 'Romania' }, { city: 'Constanța', country: 'Romania' }, { city: 'Cluj-Napoca', country: 'Romania' },
  { city: 'Moscow', country: 'Russia' }, { city: 'St. Petersburg', country: 'Russia' }, { city: 'Vladivostok', country: 'Russia' }, { city: 'Novosibirsk', country: 'Russia' }, { city: 'Novorossiysk', country: 'Russia' }, { city: 'Kaliningrad', country: 'Russia' },
  { city: 'Belgrade', country: 'Serbia' }, { city: 'Novi Sad', country: 'Serbia' },
  { city: 'Bratislava', country: 'Slovakia' },
  { city: 'Ljubljana', country: 'Slovenia' }, { city: 'Koper', country: 'Slovenia' },
  { city: 'Madrid', country: 'Spain' }, { city: 'Barcelona', country: 'Spain' }, { city: 'Valencia', country: 'Spain' }, { city: 'Algeciras', country: 'Spain' }, { city: 'Bilbao', country: 'Spain' }, { city: 'Seville', country: 'Spain' },
  { city: 'Stockholm', country: 'Sweden' }, { city: 'Gothenburg', country: 'Sweden' }, { city: 'Malmö', country: 'Sweden' },
  { city: 'Zurich', country: 'Switzerland' }, { city: 'Geneva', country: 'Switzerland' }, { city: 'Basel', country: 'Switzerland' }, { city: 'Bern', country: 'Switzerland' },
  { city: 'Istanbul', country: 'Turkey' }, { city: 'Ankara', country: 'Turkey' }, { city: 'Izmir', country: 'Turkey' }, { city: 'Mersin', country: 'Turkey' }, { city: 'Antalya', country: 'Turkey' }, { city: 'Trabzon', country: 'Turkey' },
  { city: 'Kyiv', country: 'Ukraine' }, { city: 'Odessa', country: 'Ukraine' }, { city: 'Kharkiv', country: 'Ukraine' },
  { city: 'London', country: 'UK' }, { city: 'Manchester', country: 'UK' }, { city: 'Birmingham', country: 'UK' }, { city: 'Liverpool', country: 'UK' }, { city: 'Glasgow', country: 'UK' }, { city: 'Edinburgh', country: 'UK' }, { city: 'Felixstowe', country: 'UK' }, { city: 'Southampton', country: 'UK' }, { city: 'Tilbury', country: 'UK' },

  // ── North America ──
  { city: "St. John's", country: 'Antigua and Barbuda' },
  { city: 'Nassau', country: 'Bahamas' }, { city: 'Freeport', country: 'Bahamas' },
  { city: 'Bridgetown', country: 'Barbados' },
  { city: 'Belize City', country: 'Belize' },
  { city: 'Hamilton', country: 'Bermuda' },
  { city: 'Toronto', country: 'Canada' }, { city: 'Vancouver', country: 'Canada' }, { city: 'Montreal', country: 'Canada' }, { city: 'Calgary', country: 'Canada' }, { city: 'Edmonton', country: 'Canada' }, { city: 'Halifax', country: 'Canada' }, { city: 'Winnipeg', country: 'Canada' }, { city: 'Ottawa', country: 'Canada' }, { city: 'Prince Rupert', country: 'Canada' },
  { city: 'San José', country: 'Costa Rica' }, { city: 'Limón', country: 'Costa Rica' },
  { city: 'Havana', country: 'Cuba' }, { city: 'Santiago de Cuba', country: 'Cuba' },
  { city: 'Roseau', country: 'Dominica' },
  { city: 'Santo Domingo', country: 'Dominican Republic' }, { city: 'Puerto Plata', country: 'Dominican Republic' },
  { city: 'San Salvador', country: 'El Salvador' },
  { city: 'St. George\'s', country: 'Grenada' },
  { city: 'Guatemala City', country: 'Guatemala' }, { city: 'Puerto Quetzal', country: 'Guatemala' },
  { city: 'Port-au-Prince', country: 'Haiti' },
  { city: 'Tegucigalpa', country: 'Honduras' }, { city: 'San Pedro Sula', country: 'Honduras' }, { city: 'Puerto Cortés', country: 'Honduras' },
  { city: 'Kingston', country: 'Jamaica' },
  { city: 'Mexico City', country: 'Mexico' }, { city: 'Monterrey', country: 'Mexico' }, { city: 'Guadalajara', country: 'Mexico' }, { city: 'Veracruz', country: 'Mexico' }, { city: 'Manzanillo', country: 'Mexico' }, { city: 'Lázaro Cárdenas', country: 'Mexico' }, { city: 'Cancún', country: 'Mexico' },
  { city: 'Managua', country: 'Nicaragua' }, { city: 'Corinto', country: 'Nicaragua' },
  { city: 'Panama City', country: 'Panama' }, { city: 'Colón', country: 'Panama' }, { city: 'Balboa', country: 'Panama' },
  { city: 'Port of Spain', country: 'Trinidad and Tobago' }, { city: 'Point Lisas', country: 'Trinidad and Tobago' },
  { city: 'New York', country: 'USA' }, { city: 'Los Angeles', country: 'USA' }, { city: 'Chicago', country: 'USA' }, { city: 'Houston', country: 'USA' }, { city: 'Miami', country: 'USA' }, { city: 'San Francisco', country: 'USA' }, { city: 'Seattle', country: 'USA' }, { city: 'Dallas', country: 'USA' }, { city: 'Atlanta', country: 'USA' }, { city: 'Boston', country: 'USA' }, { city: 'Philadelphia', country: 'USA' }, { city: 'Newark', country: 'USA' }, { city: 'Savannah', country: 'USA' }, { city: 'Norfolk', country: 'USA' }, { city: 'Long Beach', country: 'USA' }, { city: 'Charleston', country: 'USA' }, { city: 'Detroit', country: 'USA' }, { city: 'Denver', country: 'USA' }, { city: 'San Diego', country: 'USA' }, { city: 'Phoenix', country: 'USA' }, { city: 'Memphis', country: 'USA' }, { city: 'Baltimore', country: 'USA' },

  // ── South America ──
  { city: 'Buenos Aires', country: 'Argentina' }, { city: 'Rosario', country: 'Argentina' }, { city: 'Córdoba', country: 'Argentina' }, { city: 'Mendoza', country: 'Argentina' },
  { city: 'La Paz', country: 'Bolivia' }, { city: 'Santa Cruz', country: 'Bolivia' },
  { city: 'São Paulo', country: 'Brazil' }, { city: 'Rio de Janeiro', country: 'Brazil' }, { city: 'Santos', country: 'Brazil' }, { city: 'Paranaguá', country: 'Brazil' }, { city: 'Recife', country: 'Brazil' }, { city: 'Salvador', country: 'Brazil' }, { city: 'Manaus', country: 'Brazil' }, { city: 'Curitiba', country: 'Brazil' }, { city: 'Fortaleza', country: 'Brazil' }, { city: 'Brasília', country: 'Brazil' },
  { city: 'Santiago', country: 'Chile' }, { city: 'Valparaíso', country: 'Chile' }, { city: 'San Antonio', country: 'Chile' }, { city: 'Concepción', country: 'Chile' },
  { city: 'Bogotá', country: 'Colombia' }, { city: 'Cartagena', country: 'Colombia' }, { city: 'Medellín', country: 'Colombia' }, { city: 'Buenaventura', country: 'Colombia' }, { city: 'Barranquilla', country: 'Colombia' },
  { city: 'Quito', country: 'Ecuador' }, { city: 'Guayaquil', country: 'Ecuador' },
  { city: 'Georgetown', country: 'Guyana' },
  { city: 'Asunción', country: 'Paraguay' }, { city: 'Ciudad del Este', country: 'Paraguay' },
  { city: 'Lima', country: 'Peru' }, { city: 'Callao', country: 'Peru' }, { city: 'Arequipa', country: 'Peru' },
  { city: 'Paramaribo', country: 'Suriname' },
  { city: 'Montevideo', country: 'Uruguay' },
  { city: 'Caracas', country: 'Venezuela' }, { city: 'Maracaibo', country: 'Venezuela' }, { city: 'Puerto Cabello', country: 'Venezuela' },

  // ── Oceania ──
  { city: 'Sydney', country: 'Australia' }, { city: 'Melbourne', country: 'Australia' }, { city: 'Brisbane', country: 'Australia' }, { city: 'Perth', country: 'Australia' }, { city: 'Adelaide', country: 'Australia' }, { city: 'Fremantle', country: 'Australia' }, { city: 'Darwin', country: 'Australia' }, { city: 'Hobart', country: 'Australia' },
  { city: 'Suva', country: 'Fiji' }, { city: 'Lautoka', country: 'Fiji' },
  { city: 'Tarawa', country: 'Kiribati' },
  { city: 'Majuro', country: 'Marshall Islands' },
  { city: 'Palikir', country: 'Micronesia' },
  { city: 'Yaren', country: 'Nauru' },
  { city: 'Auckland', country: 'New Zealand' }, { city: 'Wellington', country: 'New Zealand' }, { city: 'Christchurch', country: 'New Zealand' }, { city: 'Tauranga', country: 'New Zealand' },
  { city: 'Koror', country: 'Palau' },
  { city: 'Port Moresby', country: 'Papua New Guinea' }, { city: 'Lae', country: 'Papua New Guinea' },
  { city: 'Apia', country: 'Samoa' },
  { city: 'Honiara', country: 'Solomon Islands' },
  { city: "Nuku'alofa", country: 'Tonga' },
  { city: 'Funafuti', country: 'Tuvalu' },
  { city: 'Port Vila', country: 'Vanuatu' },
  { city: 'Nouméa', country: 'New Caledonia' },
  { city: 'Papeete', country: 'French Polynesia' },
  { city: 'Hagåtña', country: 'Guam' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// ─── Helpers ───
const emptyDimension = () => ({
  length: '', width: '', height: '', boxes: '', id: Date.now() + Math.random(),
});

const initialForm = () => ({
  date: '',
  shipmentType: '',        // FCL / LCL (Sea only)
  originPort: '',           // Export: Indian port; Import: not used
  originCountry: '',        // Import: origin country
  originCity: '',           // Import: origin city (international)
  destinationPort: '',      // Import: Indian port  
  destinationCountry: '',   // Export: destination country
  destinationCity: '',      // Export: destination city (international)
  containers: '',           // Sea only
  containerType: '',        // Sea only
  packages: '',
  weight: '',
  dimensions: [emptyDimension()],
  commodity: '',
  incoterm: '',
  services: [],
  additionalNote: '',
  file: null,
});

// Only allow digits and one decimal point
const numericOnly = (val) => val.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1');
const intOnly = (val) => val.replace(/[^0-9]/g, '');

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── EmailJS Config ───
const EMAILJS_SERVICE_ID = 'service_a2m4hkr';
const EMAILJS_TEMPLATE_ID = 'template_1hlh0jc';
const EMAILJS_PUBLIC_KEY = '3EKQKmlOIiCUImLop';

const sendBookingEmail = ({ user, bookingData, bookingId }) => {
  const origin = bookingData.originPort || `${bookingData.originCity || ''}${bookingData.originCountry ? ', ' + bookingData.originCountry : ''}`;
  const destination = bookingData.destinationPort || `${bookingData.destinationCity || ''}${bookingData.destinationCountry ? ', ' + bookingData.destinationCountry : ''}`;

  const templateParams = {
    user_name: user.name || user.username || 'User',
    company_name: user.companyName || user.username || 'N/A',
    booking_type: bookingData.type || 'N/A',
    booking_id: String(bookingId || ''),
    booking_date: bookingData.date ? formatDate(bookingData.date) : 'N/A',
    origin: origin || 'N/A',
    destination: destination || 'N/A',
    packages: bookingData.packages ? `${bookingData.packages} Pkgs` : 'N/A',
    weight: bookingData.weight ? `${bookingData.weight} kg` : 'N/A',
    commodity: bookingData.commodity || 'N/A',
    shipment_type: bookingData.shipmentType || bookingData.type || 'N/A',
    to_email: user.email || '',
    from_email: 'courier@quicklook.in',
    dashboard_url: 'https://client.hmrspedition.com/user/new-booking',
    support_email: 'support@hmrspedition.com',
    current_year: String(new Date().getFullYear()),
  };

  emailjs
    .send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY)
    .then((response) => {
      console.log('📧 Booking email sent successfully!', response);
    })
    .catch((error) => {
      console.error('📧 Failed to send booking email:', error);
    });
};

// ────────────────────────────────────────────────────────────────────────
// Custom Calendar Date Picker
// ────────────────────────────────────────────────────────────────────────
const CalendarPicker = ({ value, onChange, onClose }) => {
  const today = new Date();
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const [viewYear, setViewYear] = useState(selected ? selected.getFullYear() : today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected ? selected.getMonth() : today.getMonth());

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  // Can't go to a month before the current month
  const canGoPrev = viewYear > today.getFullYear() || (viewYear === today.getFullYear() && viewMonth > today.getMonth());

  const prevMonth = () => {
    if (!canGoPrev) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  // A day is in the past if it's before today
  const isPast = (day) => {
    if (viewYear < today.getFullYear()) return true;
    if (viewYear === today.getFullYear() && viewMonth < today.getMonth()) return true;
    if (viewYear === today.getFullYear() && viewMonth === today.getMonth() && day < today.getDate()) return true;
    return false;
  };

  const selectDate = (day) => {
    if (isPast(day)) return;
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${viewYear}-${m}-${d}`);
    onClose();
  };

  const isToday = (day) =>
    day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

  const isSelected = (day) => {
    if (!selected) return false;
    return day === selected.getDate() && viewMonth === selected.getMonth() && viewYear === selected.getFullYear();
  };

  const blanks = Array.from({ length: firstDay }, (_, i) => i);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div className="w-[300px] sm:w-[320px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden select-none">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3 flex items-center justify-between">
        <button type="button" onClick={prevMonth} className={`p-1.5 rounded-lg transition ${canGoPrev ? 'hover:bg-white/20' : 'opacity-30 cursor-not-allowed'}`} disabled={!canGoPrev}>
          <ChevronLeft size={18} className="text-white" />
        </button>
        <span className="text-white font-semibold text-sm tracking-wide">
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-white/20 transition">
          <ChevronRight size={18} className="text-white" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 px-3 pt-3 pb-1">
        {DAYS.map((d) => (
          <div key={d} className="text-center text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1 px-3 pb-3">
        {blanks.map((b) => (
          <div key={`b-${b}`} />
        ))}
        {days.map((day) => (
          <button
            key={day}
            type="button"
            onClick={() => selectDate(day)}
            disabled={isPast(day)}
            className={`h-9 w-full rounded-lg text-sm font-medium transition-all ${
              isPast(day)
                ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                : isSelected(day)
                ? 'bg-blue-500 text-white shadow-md shadow-blue-500/30 scale-105'
                : isToday(day)
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 ring-1 ring-blue-300 dark:ring-blue-600'
                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            {day}
          </button>
        ))}
      </div>

      {/* Today shortcut */}
      <div className="border-t border-slate-100 dark:border-slate-700 px-3 py-2 flex justify-center">
        <button
          type="button"
          onClick={() => {
            const m = String(today.getMonth() + 1).padStart(2, '0');
            const d = String(today.getDate()).padStart(2, '0');
            onChange(`${today.getFullYear()}-${m}-${d}`);
            onClose();
          }}
          className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
        >
          Today
        </button>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Reusable Input Wrapper
// ────────────────────────────────────────────────────────────────────────
const InputField = ({ icon: Icon, iconColor, label, required, error, children }) => (
  <div>
    <label className="flex items-center gap-2 text-[13px] font-semibold text-slate-600 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
      <Icon size={15} className={iconColor} />
      {label}
      {required && <span className="text-red-400 text-base leading-none">*</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-xs font-medium text-red-500 dark:text-red-400 flex items-center gap-1">
        <span className="inline-block w-1 h-1 rounded-full bg-red-500 dark:bg-red-400" />
        {error}
      </p>
    )}
  </div>
);

const inputClass =
  'w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all text-sm outline-none';

const errorInputClass =
  'w-full px-4 py-2.5 rounded-xl border border-red-400 dark:border-red-500 bg-red-50/30 dark:bg-red-900/10 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-red-500/40 focus:border-red-400 dark:focus:border-red-500 transition-all text-sm outline-none';

// ────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────
const NewBooking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const calendarRef = useRef(null);
  const originRef = useRef(null);
  const destRef = useRef(null);

  const [activeTab, setActiveTab] = useState('Air-Export');
  const [form, setForm] = useState(initialForm());
  const [showCalendar, setShowCalendar] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showIncotermDropdown, setShowIncotermDropdown] = useState(false);
  const [showOriginSuggestions, setShowOriginSuggestions] = useState(false);
  const [showDestSuggestions, setShowDestSuggestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [pageTab, setPageTab] = useState('new-booking');
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [existingInvPl, setExistingInvPl] = useState(null); // S3 key of already uploaded INV/PL (edit mode)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBookingId, setDeletingBookingId] = useState(null);
  const [deletingBooking, setDeletingBooking] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ─── Tab type helpers ───
  const isAir = activeTab.includes('Air');
  const isSea = activeTab.includes('Sea');
  const isExport = activeTab.includes('Export');
  const isImport = activeTab.includes('Import');
  const dimUnit = 'cm';
  const dimDivisor = isAir ? 6000 : 1000000; // Air: cm→kg vol wt, Sea: cm→CBM

  // Port dropdown states
  const [showOriginPortDropdown, setShowOriginPortDropdown] = useState(false);
  const [showDestPortDropdown, setShowDestPortDropdown] = useState(false);
  const [showOriginCountryDropdown, setShowOriginCountryDropdown] = useState(false);
  const [showDestCountryDropdown, setShowDestCountryDropdown] = useState(false);
  const [showShipmentTypeDropdown, setShowShipmentTypeDropdown] = useState(false);
  const [showContainerTypeDropdown, setShowContainerTypeDropdown] = useState(false);
  const originPortRef = useRef(null);
  const destPortRef = useRef(null);
  const originCountryRef = useRef(null);
  const destCountryRef = useRef(null);

  // Filter bookings by the currently active shipment tab (Air-Export, Sea-Export, etc.)
  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => b.type === activeTab);
  }, [bookings, activeTab]);

  // ─── Fetch user bookings ───
  const fetchBookings = useCallback(async () => {
    if (!user?.id) return;
    setBookingsLoading(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.GET_USER_BOOKINGS}/${user.id}`);
      if (res.ok) {
        const data = await res.json();
        setBookings(Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setBookingsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Close calendar on outside click
  useEffect(() => {
    const handler = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCalendar]);

  // Close origin suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (originRef.current && !originRef.current.contains(e.target)) setShowOriginSuggestions(false);
    };
    if (showOriginSuggestions) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOriginSuggestions]);

  // Close destination suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (destRef.current && !destRef.current.contains(e.target)) setShowDestSuggestions(false);
    };
    if (showDestSuggestions) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDestSuggestions]);

  // ─── Filtered port/country lists ───
  const portList = isSea ? INDIAN_SEA_PORTS : INDIAN_AIR_PORTS;

  const filteredOriginPorts = useMemo(() => {
    const q = form.originPort.trim().toLowerCase();
    if (!q) return portList;
    return portList.filter((p) => p.toLowerCase().includes(q));
  }, [form.originPort, portList]);

  const filteredDestPorts = useMemo(() => {
    const q = form.destinationPort.trim().toLowerCase();
    if (!q) return portList;
    return portList.filter((p) => p.toLowerCase().includes(q));
  }, [form.destinationPort, portList]);

  const filteredOriginCountries = useMemo(() => {
    const q = form.originCountry.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.toLowerCase().includes(q));
  }, [form.originCountry]);

  const filteredDestCountries = useMemo(() => {
    const q = form.destinationCountry.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.toLowerCase().includes(q));
  }, [form.destinationCountry]);

  // ─── Auto-calculate total volumetric weight / CBM from dimensions ───
  const totalVolWeight = useMemo(() => {
    let total = 0;
    form.dimensions.forEach((d) => {
      const l = parseFloat(d.length) || 0;
      const w = parseFloat(d.width) || 0;
      const h = parseFloat(d.height) || 0;
      const boxes = parseInt(d.boxes) || 1;
      if (l > 0 && w > 0 && h > 0) {
        if (isSea) {
          // Sea: CBM = (L cm × W cm × H cm) / 1,000,000 × boxes
          total += (l * w * h / 1000000) * boxes;
        } else {
          // Air: Volume Weight = (L cm × W cm × H cm) / 6,000 × boxes  
          total += (l * w * h / 6000) * boxes;
        }
      }
    });
    return total;
  }, [form.dimensions, isSea]);

  // ─── Handlers ───
  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setForm(initialForm());
    setErrors({});
    setEditingBooking(null);
  };

  // ─── Edit booking handler ───
  const handleEdit = (booking) => {
    setEditingBooking(booking);
    setActiveTab(booking.type);
    setPageTab('new-booking');

    const dims = (
      Array.isArray(booking.dimensions)
        ? booking.dimensions
        : typeof booking.dimensions === 'string'
          ? JSON.parse(booking.dimensions)
          : []
    ).map((d) => ({ ...d, id: Date.now() + Math.random() }));

    // Carry over existing invPl S3 key so UI shows the uploaded PDF
    setExistingInvPl(booking.invPl || null);

    setForm({
      date: booking.date || '',
      shipmentType: booking.shipmentType || '',
      originPort: booking.originPort || '',
      originCountry: booking.originCountry || '',
      originCity: booking.originCity || '',
      destinationPort: booking.destinationPort || '',
      destinationCountry: booking.destinationCountry || '',
      destinationCity: booking.destinationCity || '',
      containers: booking.containers || '',
      containerType: booking.containerType || '',
      packages: booking.packages || '',
      weight: booking.weight || '',
      dimensions: dims.length > 0 ? dims : [emptyDimension()],
      commodity: booking.commodity || '',
      incoterm: booking.incoterm || '',
      services: Array.isArray(booking.services)
        ? booking.services
        : typeof booking.services === 'string'
          ? JSON.parse(booking.services)
          : [],
      additionalNote: booking.additionalNote || '',
      file: null,
    });
    setErrors({});
    setExpandedBooking(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── Delete booking handler ───
  const handleDeleteConfirm = async () => {
    if (!deletingBookingId) return;
    setDeleting(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.NEW_BOOKING}/${deletingBookingId}`, {
        method: 'DELETE',
      });
      if (res.ok || res.status === 204) {
        toast.success('Booking deleted successfully');
        sendBookingStatusEmail({
          to_email: user?.email || '',
          name: user?.name || user?.username || 'User',
          booking_id: deletingBookingId,
          service_name: deletingBooking?.type || '',
          booking_date: deletingBooking?.date ? formatDate(deletingBooking.date) : '',
          booking_status: 'Cancelled',
        }).catch(console.error);
        fetchBookings();
      } else {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to delete booking');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.message || 'Failed to delete booking');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeletingBookingId(null);
      setDeletingBooking(null);
    }
  };

  const handleDimensionChange = (index, field, value) => {
    const cleaned = field === 'boxes' ? intOnly(value) : numericOnly(value);
    setForm((prev) => {
      const dims = [...prev.dimensions];
      dims[index] = { ...dims[index], [field]: cleaned };
      return { ...prev, dimensions: dims };
    });
  };

  const addDimension = () => {
    setForm((prev) => ({
      ...prev,
      dimensions: [...prev.dimensions, emptyDimension()],
    }));
  };

  const removeDimension = (index) => {
    if (form.dimensions.length <= 1) return;
    setForm((prev) => ({
      ...prev,
      dimensions: prev.dimensions.filter((_, i) => i !== index),
    }));
  };

  const isDimFilled = (dim) => dim.length && dim.width && dim.height;

  // Services
  const toggleService = (service) => {
    setForm((prev) => {
      let updated;
      if (service === 'All') {
        updated = prev.services.length === SERVICE_OPTIONS.length - 1
          ? []
          : SERVICE_OPTIONS.filter((s) => s !== 'All');
      } else {
        updated = prev.services.includes(service)
          ? prev.services.filter((s) => s !== service)
          : [...prev.services, service];
      }
      return { ...prev, services: updated };
    });
    setErrors((prev) => {
      if (prev.services) {
        const next = { ...prev };
        delete next.services;
        return next;
      }
      return prev;
    });
  };
  const isAllSelected = form.services.length === SERVICE_OPTIONS.length - 1;

  // File
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(file.type)) { toast.error('Only PDF, JPG, PNG files are allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File size must be less than 10 MB'); return; }
    handleChange('file', file);
  };
  const removeFile = () => { handleChange('file', null); setExistingInvPl(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields based on tab type
    const newErrors = {};
    if (!form.date) newErrors.date = 'Please select a date';
    if (!form.packages) newErrors.packages = 'Please enter total packages';
    if (!form.weight) newErrors.weight = 'Please enter total weight';
    if (!form.commodity.trim()) newErrors.commodity = 'Please enter commodity';
    if (form.services.length === 0) newErrors.services = 'Please select at least one service';

    if (isExport) {
      // Export: needs origin port, dest country, dest city
      if (!form.originPort.trim()) newErrors.originPort = 'Please select origin port';
      if (!form.destinationCountry.trim()) newErrors.destinationCountry = 'Please select destination country';
      if (!form.destinationCity.trim()) newErrors.destinationCity = 'Please select destination city';
    } else {
      // Import: needs dest port, origin country, origin city
      if (!form.destinationPort.trim()) newErrors.destinationPort = 'Please select destination port';
      if (!form.originCountry.trim()) newErrors.originCountry = 'Please select origin country';
      if (!form.originCity.trim()) newErrors.originCity = 'Please select origin city';
    }

    if (isSea) {
      // Sea tabs also need shipment type
      if (!form.shipmentType) newErrors.shipmentType = 'Please select shipment type';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill all required fields');
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      let invPlKey = existingInvPl || ''; // preserve existing S3 key if editing and no new file selected

      // Step 1: If file exists, get presigned URL from upload-url API, then upload file to S3
      if (form.file) {
        const uploadRes = await apiCall(API_ENDPOINTS.NEW_BOOKING_UPLOAD_URL, {
          method: 'POST',
          body: JSON.stringify({
            userId: user.id,
            fileName: form.file.name,
            contentType: form.file.type,
          }),
        });

        if (!uploadRes.ok) {
          const errData = await uploadRes.json().catch(() => ({}));
          throw new Error(errData.message || 'Failed to get upload URL');
        }

        const uploadData = await uploadRes.json();
        const presignedUrl = uploadData.data?.uploadUrl || '';
        invPlKey = uploadData.data?.key || '';
        console.log('📎 Got S3 key:', invPlKey);

        // Upload file to S3 using presigned URL
        if (presignedUrl) {
          const s3Res = await fetch(presignedUrl, {
            method: 'PUT',
            headers: { 'Content-Type': form.file.type },
            body: form.file,
          });
          if (!s3Res.ok) {
            throw new Error('Failed to upload file to S3');
          }
          console.log('✅ File uploaded to S3 successfully');
        }
      }

      // Step 2: Submit booking
      const payload = {
        userId: user.id,
        type: activeTab,
        date: form.date,
        shipmentType: isSea ? form.shipmentType : undefined,
        originPort: isExport ? form.originPort : undefined,
        originCountry: isImport ? form.originCountry : undefined,
        originCity: isImport ? form.originCity : undefined,
        destinationPort: isImport ? form.destinationPort : undefined,
        destinationCountry: isExport ? form.destinationCountry : undefined,
        destinationCity: isExport ? form.destinationCity : undefined,
        containers: isSea ? form.containers : undefined,
        containerType: isSea ? form.containerType : undefined,
        packages: form.packages,
        weight: form.weight,
        dimensions: form.dimensions
          .filter((d) => d.length && d.width && d.height)
          .map(({ length, width, height, boxes }) => ({ length, width, height, boxes: boxes || '1' })),
        volume: totalVolWeight.toFixed(3),
        commodity: form.commodity,
        incoterm: form.incoterm,
        services: form.services,
        additionalNote: form.additionalNote,
        invPl: invPlKey,
      };

      console.log('📦 Booking payload:', payload);

      const isEditing = !!editingBooking;
      const bookingRes = await apiCall(
        isEditing ? `${API_ENDPOINTS.NEW_BOOKING}/${editingBooking.id}` : API_ENDPOINTS.NEW_BOOKING,
        {
          method: isEditing ? 'PUT' : 'POST',
          body: JSON.stringify(payload),
        }
      );

      if (!bookingRes.ok) {
        const errData = await bookingRes.json().catch(() => ({}));
        throw new Error(errData.message || (isEditing ? 'Failed to update booking' : 'Failed to submit booking'));
      }

      const bookingResData = await bookingRes.json().catch(() => ({}));

      const bookingId = bookingResData?.data?.id || editingBooking?.id || '';
      sendBookingStatusEmail({
        to_email: user?.email || '',
        name: user?.name || user?.username || 'User',
        booking_id: bookingId,
        service_name: payload.type || activeTab,
        booking_date: payload.date ? formatDate(payload.date) : '',
        booking_status: isEditing ? 'Modified' : 'Confirmed',
      }).catch(console.error);

      toast.success(isEditing ? 'Booking updated successfully!' : 'Booking request submitted successfully!');
      setForm(initialForm());
      setEditingBooking(null);
      setExistingInvPl(null);
      setErrors({});
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchBookings(); // Refresh bookings list
    } catch (err) {
      console.error('Booking error:', err);
      toast.error(err.message || 'Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const tabLabel = activeTab.replace('-', ' ');

  return (
    <UserShell activeTab={activeTab}>
      <div className="max-w-6xl mx-auto">
        {/* ─── Back + Title ─── */}
        <div className="flex items-center gap-3 mb-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            title="Back"
            className="grid place-items-center w-10 h-10 rounded-xl flex-shrink-0 transition
                       border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700
                       dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400 dark:hover:bg-slate-700/60"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
              {tabLabel}
            </p>
            <h1 className="text-[26px] font-black text-slate-900 dark:text-white tracking-tight leading-tight">
              New Booking
            </h1>
          </div>
        </div>

        {/* ─── Shipment Type Tabs ─── */}
        <ShipmentTabs activeTab={activeTab} onTabChange={handleTabChange} />

        {/* ─── Page Tabs: New Booking / Bookings History ─── */}
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800/70 mb-5">
          <button
            type="button"
            onClick={() => setPageTab('new-booking')}
            className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-lg text-[13px] font-bold transition ${
              pageTab === 'new-booking'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
            }`}
          >
            <PlusCircle size={16} />
            New Booking
          </button>
          <button
            type="button"
            onClick={() => { setPageTab('history'); fetchBookings(); }}
            className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-lg text-[13px] font-bold transition ${
              pageTab === 'history'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
            }`}
          >
            <History size={16} />
            Bookings History
            {filteredBookings.length > 0 && (
              <span className={`px-1.5 rounded-md text-[11px] font-bold ${
                pageTab === 'history' ? 'bg-white/20 text-white' : 'bg-slate-200 dark:bg-slate-700'
              }`}>
                {filteredBookings.length}
              </span>
            )}
          </button>
        </div>

        {/* ──────────────────────────────────── */}
        {/* NEW BOOKING TAB */}
        {/* ──────────────────────────────────── */}
        {pageTab === 'new-booking' && (
          <>

        {/* ─── Form ─── */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden
                     dark:border-slate-800 dark:bg-slate-900/60"
        >
          {/* amber while editing an existing booking, so the state is obvious */}
          <div className={`h-1 ${editingBooking ? 'bg-amber-500' : 'bg-blue-600'}`} />

          {/* Editing Banner */}
          {editingBooking && (
            <div className="mx-4 sm:mx-6 lg:mx-8 mt-4 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <Pencil size={16} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Editing Booking #{editingBooking.id}</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 truncate">
                  {editingBooking.originPort || editingBooking.originCity || '—'} → {editingBooking.destinationPort || editingBooking.destinationCity || '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setEditingBooking(null); setExistingInvPl(null); setForm(initialForm()); setErrors({}); }}
                className="p-1.5 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition flex-shrink-0"
                title="Cancel editing"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <div className="p-4 sm:p-6 lg:p-8 space-y-7">

            {/* ═══ SEA: Shipment Type (Row 0) ═══ */}
            {isSea && (
              <InputField icon={Package} iconColor="text-blue-500" label="Shipment Type" required error={errors.shipmentType}>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setShowShipmentTypeDropdown(!showShipmentTypeDropdown); setShowServiceDropdown(false); setShowIncotermDropdown(false); setShowCalendar(false); }}
                    className={`${errors.shipmentType ? errorInputClass : inputClass} text-left flex items-center justify-between cursor-pointer`}
                  >
                    <span className={form.shipmentType ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-600'}>
                      {form.shipmentType || 'Select'}
                    </span>
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${showShipmentTypeDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showShipmentTypeDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowShipmentTypeDropdown(false)} />
                      <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                        {SHIPMENT_TYPE_OPTIONS.map((t) => (
                          <button key={t} type="button" onClick={() => { handleChange('shipmentType', t); setShowShipmentTypeDropdown(false); }}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center justify-between ${form.shipmentType === t ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                            {t} {form.shipmentType === t && <Check size={16} className="text-blue-500" />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </InputField>
            )}

            {/* ═══ Row 1: Origin Port/Dest Port + Planning Date ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* EXPORT → Origin Port | IMPORT → Destination Port */}
              {isExport ? (
                <InputField icon={MapPin} iconColor="text-emerald-500" label="Origin Port" required error={errors.originPort}>
                  <div className="relative" ref={originPortRef}>
                    <input
                      type="text"
                      value={form.originPort}
                      onChange={(e) => { handleChange('originPort', e.target.value); setShowOriginPortDropdown(true); }}
                      onFocus={() => setShowOriginPortDropdown(true)}
                      placeholder="Select Origin"
                      className={errors.originPort ? errorInputClass : inputClass}
                      autoComplete="off"
                    />
                    {showOriginPortDropdown && filteredOriginPorts.length > 0 && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowOriginPortDropdown(false)} />
                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                          {filteredOriginPorts.map((p) => (
                            <button key={p} type="button" onClick={() => { handleChange('originPort', p); setShowOriginPortDropdown(false); }}
                              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition ${form.originPort === p ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                              {p}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </InputField>
              ) : (
                <InputField icon={MapPin} iconColor="text-rose-500" label="Destination Port" required error={errors.destinationPort}>
                  <div className="relative" ref={destPortRef}>
                    <input
                      type="text"
                      value={form.destinationPort}
                      onChange={(e) => { handleChange('destinationPort', e.target.value); setShowDestPortDropdown(true); }}
                      onFocus={() => setShowDestPortDropdown(true)}
                      placeholder="Select Destination"
                      className={errors.destinationPort ? errorInputClass : inputClass}
                      autoComplete="off"
                    />
                    {showDestPortDropdown && filteredDestPorts.length > 0 && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowDestPortDropdown(false)} />
                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                          {filteredDestPorts.map((p) => (
                            <button key={p} type="button" onClick={() => { handleChange('destinationPort', p); setShowDestPortDropdown(false); }}
                              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition ${form.destinationPort === p ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                              {p}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </InputField>
              )}

              {/* Planning Date */}
              <InputField icon={Calendar} iconColor="text-blue-500" label="Planning Date" required error={errors.date}>
                <div className="relative" ref={calendarRef}>
                  <button
                    type="button"
                    onClick={() => { setShowCalendar(!showCalendar); setShowServiceDropdown(false); setShowIncotermDropdown(false); }}
                    className={`${errors.date ? errorInputClass : inputClass} text-left flex items-center justify-between cursor-pointer`}
                  >
                    <span className={form.date ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-600'}>
                      {form.date ? formatDate(form.date) : 'dd/mm/yyyy'}
                    </span>
                    <Calendar size={16} className="text-slate-400 flex-shrink-0" />
                  </button>
                  {showCalendar && (
                    <div className="absolute z-30 mt-2 left-0 sm:left-auto">
                      <CalendarPicker
                        value={form.date}
                        onChange={(d) => handleChange('date', d)}
                        onClose={() => setShowCalendar(false)}
                      />
                    </div>
                  )}
                </div>
              </InputField>
            </div>

            {/* ═══ SEA: Containers + Container Type ═══ */}
            {isSea && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <InputField icon={Package} iconColor="text-orange-500" label="No. of Containers">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.containers}
                    onChange={(e) => handleChange('containers', intOnly(e.target.value))}
                    placeholder=""
                    className={inputClass}
                  />
                </InputField>

                <InputField icon={Package} iconColor="text-teal-500" label="Container Type">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => { setShowContainerTypeDropdown(!showContainerTypeDropdown); setShowServiceDropdown(false); setShowIncotermDropdown(false); setShowCalendar(false); }}
                      className={`${inputClass} text-left flex items-center justify-between cursor-pointer`}
                    >
                      <span className={form.containerType ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-600'}>
                        {form.containerType || 'Select Container'}
                      </span>
                      <ChevronDown size={16} className={`text-slate-400 transition-transform ${showContainerTypeDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showContainerTypeDropdown && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowContainerTypeDropdown(false)} />
                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                          {CONTAINER_TYPE_OPTIONS.map((ct) => (
                            <button key={ct} type="button" onClick={() => { handleChange('containerType', ct); setShowContainerTypeDropdown(false); }}
                              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center justify-between ${form.containerType === ct ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                              {ct} {form.containerType === ct && <Check size={16} className="text-blue-500" />}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </InputField>
              </div>
            )}

            {/* ═══ Row 2: Country + City (Export → Destination, Import → Origin) ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {isExport ? (
                <>
                  <InputField icon={MapPin} iconColor="text-rose-500" label="Destination Country" required error={errors.destinationCountry}>
                    <div className="relative" ref={destCountryRef}>
                      <input
                        type="text"
                        value={form.destinationCountry}
                        onChange={(e) => { handleChange('destinationCountry', e.target.value); handleChange('destinationCity', ''); setShowDestCountryDropdown(true); }}
                        onFocus={() => setShowDestCountryDropdown(true)}
                        placeholder="Select Country"
                        className={errors.destinationCountry ? errorInputClass : inputClass}
                        autoComplete="off"
                      />
                      {showDestCountryDropdown && filteredDestCountries.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowDestCountryDropdown(false)} />
                          <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                            {filteredDestCountries.map((c) => (
                              <button key={c} type="button" onClick={() => { handleChange('destinationCountry', c); setShowDestCountryDropdown(false); }}
                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center gap-2 ${form.destinationCountry === c ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                                <span className="text-base">{countryToFlag(c)}</span> {c}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </InputField>

                  <InputField icon={MapPin} iconColor="text-rose-400" label="Destination City" required error={errors.destinationCity}>
                    <div className="relative" ref={destRef}>
                      <input
                        type="text"
                        value={form.destinationCity}
                        onChange={(e) => { handleChange('destinationCity', e.target.value); setShowDestSuggestions(true); }}
                        onFocus={() => setShowDestSuggestions(true)}
                        placeholder="Select city"
                        className={errors.destinationCity ? errorInputClass : inputClass}
                        autoComplete="off"
                      />
                      {showDestSuggestions && (() => {
                        const filtered = INTERNATIONAL_CITIES.filter(
                          (c) => c.country === form.destinationCountry && (
                            !form.destinationCity.trim() || c.city.toLowerCase().includes(form.destinationCity.trim().toLowerCase())
                          )
                        );
                        return filtered.length > 0 ? (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowDestSuggestions(false)} />
                            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                              {filtered.map((item) => (
                                <button key={item.city} type="button" onClick={() => { handleChange('destinationCity', item.city); setShowDestSuggestions(false); }}
                                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition ${form.destinationCity === item.city ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                                  {item.city}
                                </button>
                              ))}
                            </div>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </InputField>
                </>
              ) : (
                <>
                  <InputField icon={MapPin} iconColor="text-emerald-500" label="Origin Country" required error={errors.originCountry}>
                    <div className="relative" ref={originCountryRef}>
                      <input
                        type="text"
                        value={form.originCountry}
                        onChange={(e) => { handleChange('originCountry', e.target.value); handleChange('originCity', ''); setShowOriginCountryDropdown(true); }}
                        onFocus={() => setShowOriginCountryDropdown(true)}
                        placeholder="Select Origin"
                        className={errors.originCountry ? errorInputClass : inputClass}
                        autoComplete="off"
                      />
                      {showOriginCountryDropdown && filteredOriginCountries.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowOriginCountryDropdown(false)} />
                          <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                            {filteredOriginCountries.map((c) => (
                              <button key={c} type="button" onClick={() => { handleChange('originCountry', c); setShowOriginCountryDropdown(false); }}
                                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center gap-2 ${form.originCountry === c ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                                <span className="text-base">{countryToFlag(c)}</span> {c}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </InputField>

                  <InputField icon={MapPin} iconColor="text-emerald-400" label="Origin City" required error={errors.originCity}>
                    <div className="relative" ref={originRef}>
                      <input
                        type="text"
                        value={form.originCity}
                        onChange={(e) => { handleChange('originCity', e.target.value); setShowOriginSuggestions(true); }}
                        onFocus={() => setShowOriginSuggestions(true)}
                        placeholder="Select city"
                        className={errors.originCity ? errorInputClass : inputClass}
                        autoComplete="off"
                      />
                      {showOriginSuggestions && (() => {
                        const filtered = INTERNATIONAL_CITIES.filter(
                          (c) => c.country === form.originCountry && (
                            !form.originCity.trim() || c.city.toLowerCase().includes(form.originCity.trim().toLowerCase())
                          )
                        );
                        return filtered.length > 0 ? (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowOriginSuggestions(false)} />
                            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                              {filtered.map((item) => (
                                <button key={item.city} type="button" onClick={() => { handleChange('originCity', item.city); setShowOriginSuggestions(false); }}
                                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition ${form.originCity === item.city ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                                  {item.city}
                                </button>
                              ))}
                            </div>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </InputField>
                </>
              )}
            </div>

            {/* ═══ Row 3: Total Package + Gr. Weight ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputField icon={Package} iconColor="text-orange-500" label={isAir ? 'Total Package (Nos)' : 'Total Package'} required error={errors.packages}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.packages}
                  onChange={(e) => handleChange('packages', intOnly(e.target.value))}
                  placeholder=""
                  className={errors.packages ? errorInputClass : inputClass}
                />
              </InputField>

              <InputField icon={Weight} iconColor="text-purple-500" label="Gr. Weight (kg)" required error={errors.weight}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.weight}
                  onChange={(e) => handleChange('weight', numericOnly(e.target.value))}
                  placeholder=""
                  className={errors.weight ? errorInputClass : inputClass}
                />
              </InputField>
            </div>

            {/* ═══ DIMENSIONS ═══ */}
            <div>
              <label className="flex items-center gap-2 text-[13px] font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wide">
                <Ruler size={15} className="text-teal-500" />
                Dimensions({dimUnit})
              </label>

              {/* Table header */}
              <div className="hidden sm:grid gap-2 mb-1.5 pl-1" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 34px' }}>
                <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Length</span>
                <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Width</span>
                <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Height</span>
                <span className="text-[11px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider">Boxes</span>
                <span></span>
              </div>

              <div className="space-y-2.5">
                {form.dimensions.map((dim, idx) => {
                  const l = parseFloat(dim.length) || 0;
                  const w = parseFloat(dim.width) || 0;
                  const h = parseFloat(dim.height) || 0;
                  const boxes = parseInt(dim.boxes) || 1;
                  const rowVal = isSea
                    ? (l * w * h / 1000000) * boxes   // CBM
                    : (l * w * h / 6000) * boxes;   // Vol Wt kg
                  const showAdd = idx === form.dimensions.length - 1 && isDimFilled(dim);
                  const showDelete = form.dimensions.length > 1;
                  return (
                    <div key={dim.id} className="group">
                      {/* Mobile: 2-col grid */}
                      <div className="grid grid-cols-2 sm:hidden gap-2">
                        <input type="text" inputMode="decimal" value={dim.length} onChange={(e) => handleDimensionChange(idx, 'length', e.target.value)} placeholder="Length" className={`${inputClass} text-center`} />
                        <input type="text" inputMode="decimal" value={dim.width} onChange={(e) => handleDimensionChange(idx, 'width', e.target.value)} placeholder="Width" className={`${inputClass} text-center`} />
                        <input type="text" inputMode="decimal" value={dim.height} onChange={(e) => handleDimensionChange(idx, 'height', e.target.value)} placeholder="Height" className={`${inputClass} text-center`} />
                        <input type="text" inputMode="numeric" value={dim.boxes} onChange={(e) => handleDimensionChange(idx, 'boxes', e.target.value)} placeholder="Boxes" className={`${inputClass} text-center !border-cyan-500 dark:!border-cyan-400 !border-2 bg-cyan-50 dark:bg-cyan-900/20 shadow-md shadow-cyan-200 dark:shadow-cyan-900/30 placeholder:text-cyan-400 dark:placeholder:text-cyan-500`} />
                        {(showDelete || showAdd) && (
                          <div className="col-span-2 flex gap-2 justify-end">
                            {showDelete && (
                              <button type="button" onClick={() => removeDimension(idx)} className="px-3 py-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition text-xs flex items-center gap-1"><Trash2 size={14} /> Remove</button>
                            )}
                            {showAdd && (
                              <button type="button" onClick={addDimension} className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition border border-blue-200 dark:border-blue-800 text-xs flex items-center gap-1"><Plus size={14} /> Add</button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Desktop: single row */}
                      <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 34px' }}>
                        <input type="text" inputMode="decimal" value={dim.length} onChange={(e) => handleDimensionChange(idx, 'length', e.target.value)} placeholder="Length" className={`${inputClass} text-center`} />
                        <input type="text" inputMode="decimal" value={dim.width} onChange={(e) => handleDimensionChange(idx, 'width', e.target.value)} placeholder="Width" className={`${inputClass} text-center`} />
                        <input type="text" inputMode="decimal" value={dim.height} onChange={(e) => handleDimensionChange(idx, 'height', e.target.value)} placeholder="Height" className={`${inputClass} text-center`} />
                        <input type="text" inputMode="numeric" value={dim.boxes} onChange={(e) => handleDimensionChange(idx, 'boxes', e.target.value)} placeholder="Boxes" className={`${inputClass} text-center !border-cyan-500 dark:!border-cyan-400 !border-2 bg-cyan-50 dark:bg-cyan-900/20 shadow-md shadow-cyan-200 dark:shadow-cyan-900/30 placeholder:text-cyan-400 dark:placeholder:text-cyan-500`} />
                        <div className="flex items-center justify-center">
                          {showDelete ? (
                            <button type="button" onClick={() => removeDimension(idx)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition" title="Remove">
                              <Trash2 size={16} />
                            </button>
                          ) : showAdd ? (
                            <button type="button" onClick={addDimension} className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition border border-blue-200 dark:border-blue-800" title="Add row">
                              <Plus size={16} />
                            </button>
                          ) : (
                            <span className="w-[34px]" />
                          )}
                        </div>
                      </div>

                      {/* Per-row volume weight */}
                      {l > 0 && w > 0 && h > 0 && (
                        <div className="flex items-center gap-3 mt-1 pl-1 text-[11px] text-indigo-500 dark:text-indigo-400 font-medium">
                          {(() => {
                            const perBox = isSea ? (l * w * h / 1000000) : (l * w * h / 6000);
                            const unit = isSea ? 'CBM' : 'kg';
                            return boxes > 1
                              ? <span>1 Box: {perBox.toFixed(3)} {unit} &nbsp;|&nbsp; {boxes} Boxes: {(perBox * boxes).toFixed(3)} {unit}</span>
                              : <span>{perBox.toFixed(3)} {unit}</span>;
                          })()}
                        </div>
                      )}

                      {showDelete && showAdd && (
                        <div className="hidden sm:flex justify-end mt-1">
                          <button type="button" onClick={addDimension} className="px-3 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition border border-blue-200 dark:border-blue-800 text-xs flex items-center gap-1">
                            <Plus size={13} /> Add Row
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ═══ Row: Volume Weight / CBM Value + Commodity ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputField icon={Weight} iconColor="text-indigo-500" label={isSea ? 'CBM Value' : 'Volume Weight (kg)'}>
                <div className={`${inputClass} bg-gradient-to-r from-indigo-50/50 to-blue-50/50 dark:from-indigo-900/10 dark:to-blue-900/10 flex items-center justify-between cursor-default`}>
                  <span className={`font-semibold ${totalVolWeight > 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-600'}`}>
                    {totalVolWeight > 0 ? totalVolWeight.toFixed(3) : '0.000'}
                  </span>
                </div>
              </InputField>

              <InputField icon={FileText} iconColor="text-amber-500" label="Commodity" required error={errors.commodity}>
                <input
                  type="text"
                  value={form.commodity}
                  onChange={(e) => handleChange('commodity', e.target.value)}
                  placeholder=""
                  className={errors.commodity ? errorInputClass : inputClass}
                />
              </InputField>
            </div>

            {/* ═══ Row: Incoterm ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputField icon={FileText} iconColor="text-rose-500" label="Incoterm">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setShowIncotermDropdown(!showIncotermDropdown); setShowServiceDropdown(false); setShowCalendar(false); }}
                    className={`${inputClass} text-left flex items-center justify-between cursor-pointer`}
                  >
                    <span className={form.incoterm ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-600'}>
                      {form.incoterm || 'Select Incoterm'}
                    </span>
                    <ChevronDown size={16} className={`text-slate-400 transition-transform ${showIncotermDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showIncotermDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowIncotermDropdown(false)} />
                      <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 max-h-52 overflow-y-auto">
                        {INCOTERM_OPTIONS.map((term) => (
                          <button key={term} type="button" onClick={() => { handleChange('incoterm', term); setShowIncotermDropdown(false); }}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex items-center justify-between ${form.incoterm === term ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold' : 'text-slate-700 dark:text-slate-300'}`}>
                            {term} {form.incoterm === term && <Check size={16} className="text-blue-500" />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </InputField>

              <InputField icon={Upload} iconColor="text-emerald-500" label="Inv/PL Upload">
                {form.file ? (
                  /* New file selected */
                  <div className={`${inputClass} flex items-center gap-2`}>
                    <FileText size={16} className="text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-slate-900 dark:text-white truncate flex-1">{form.file.name}</span>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">{(form.file.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => { const url = URL.createObjectURL(form.file); window.open(url, '_blank'); }}
                      className="p-1 rounded-md text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex-shrink-0" title="View file">
                      <Eye size={15} />
                    </button>
                    <button type="button" onClick={removeFile} className="p-1 rounded-md text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition flex-shrink-0" title="Remove">
                      <X size={15} />
                    </button>
                  </div>
                ) : existingInvPl ? (
                  /* Existing S3 file from edit (no new file chosen yet) */
                  <div className={`${inputClass} flex items-center gap-2`}>
                    <FileText size={16} className="text-emerald-500 flex-shrink-0" />
                    <span className="text-sm text-slate-900 dark:text-white truncate flex-1">
                      {typeof existingInvPl === 'string' ? existingInvPl.split('/').pop() : 'Uploaded'}
                    </span>
                    <span className="text-[10px] text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0">Saved</span>
                    <button type="button" onClick={() => {
                      const fullUrl = existingInvPl.startsWith('http') ? existingInvPl : getCloudFrontUrl(existingInvPl);
                      window.open(fullUrl, '_blank');
                    }}
                      className="p-1 rounded-md text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition flex-shrink-0" title="View file">
                      <Eye size={15} />
                    </button>
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                      className="p-1 rounded-md text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition flex-shrink-0" title="Replace file">
                      <RefreshCw size={15} />
                    </button>
                    <button type="button" onClick={removeFile} className="p-1 rounded-md text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition flex-shrink-0" title="Remove">
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  /* No file at all */
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`${inputClass} flex items-center gap-3 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-all`}
                  >
                    <Upload size={16} className="text-blue-500 flex-shrink-0" />
                    <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">File Upload</span>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />
              </InputField>
            </div>

            {/* ═══ Services Selection ═══ */}
            <div>
              <InputField icon={Package} iconColor="text-cyan-500" label="Select Services to Avail" required error={errors.services}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                  {SERVICE_OPTIONS.map((service) => {
                    const isChecked = service === 'All' ? isAllSelected : form.services.includes(service);
                    return (
                      <button
                        key={service}
                        type="button"
                        onClick={() => toggleService(service)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition border ${
                          isChecked
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition ${
                          isChecked ? 'bg-blue-500 border-blue-500' : 'border-slate-300 dark:border-slate-600'
                        }`}>
                          {isChecked && <Check size={10} className="text-white" strokeWidth={3} />}
                        </div>
                        <span className={`text-xs ${service === 'All' ? 'font-bold' : 'font-medium'}`}>
                          {service === 'All' ? 'All Services' : service}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </InputField>
            </div>

            {/* ═══ ADDITIONAL NOTE ═══ */}
            <InputField icon={FileText} iconColor="text-slate-400" label="Addl. Note">
              <textarea
                value={form.additionalNote}
                onChange={(e) => handleChange('additionalNote', e.target.value)}
                placeholder=""
                rows={3}
                className={`${inputClass} resize-y`}
              />
            </InputField>

            {/* ═══ SUBMIT ═══ */}
            <div className="pt-3 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 sm:flex-none px-10 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl shadow-blue-500/20 transition-all flex items-center justify-center gap-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.01] active:scale-[0.99]"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {editingBooking ? 'Updating...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    {editingBooking ? <Pencil size={17} /> : <Send size={17} />}
                    {editingBooking ? 'Update Booking' : 'Send Booking'}
                  </>
                )}
              </button>
              {editingBooking && (
                <button
                  type="button"
                  onClick={() => { setEditingBooking(null); setExistingInvPl(null); setForm(initialForm()); setErrors({}); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="px-6 py-3 rounded-xl border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 transition text-sm"
                >
                  Cancel Edit
                </button>
              )}
              <button
                type="button"
                onClick={() => { setEditingBooking(null); setExistingInvPl(null); setForm(initialForm()); setErrors({}); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="px-6 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition text-sm"
              >
                Reset
              </button>
            </div>

          </div>
        </form>
          </>
        )}

        {/* ──────────────────────────────────── */}
        {/* BOOKINGS HISTORY TAB */}
        {/* ──────────────────────────────────── */}
        {pageTab === 'history' && (
        <div>
          {bookingsLoading && filteredBookings.length === 0 ? (
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700/60 p-12 flex flex-col items-center gap-3">
              <Loader2 size={28} className="text-blue-500 animate-spin" />
              <p className="text-sm text-slate-400 dark:text-slate-500">Loading bookings...</p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700/60 p-12 flex flex-col items-center gap-3">
              <Package size={36} className="text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-medium text-slate-400 dark:text-slate-500">No {activeTab.replace('-', ' ')} bookings yet</p>
              <p className="text-xs text-slate-300 dark:text-slate-600">Submit your first {activeTab.replace('-', ' ').toLowerCase()} booking!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBookings.map((b, index) => {
                const isExpanded = expandedBooking === (b.id || index);
                const isAir = (b.type || '').toLowerCase().includes('air');

                return (
                  <div
                    key={b.id || index}
                    className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700/60 overflow-hidden transition-all hover:shadow-xl"
                  >
                    {/* Card Top Accent */}
                    <div className={`h-0.5 ${isAir ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gradient-to-r from-cyan-500 to-teal-500'}`} />

                    {/* Card Header — always visible */}
                    <button
                      type="button"
                      onClick={() => setExpandedBooking(isExpanded ? null : (b.id || index))}
                      className="w-full px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 text-left hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition"
                    >
                      {/* Type icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isAir ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-cyan-50 dark:bg-cyan-900/20'}`}>
                        {isAir ? <Plane size={18} className="text-blue-500" /> : <Ship size={18} className="text-cyan-500" />}
                      </div>

                      {/* Main info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-slate-900 dark:text-white truncate">
                            {b.originPort || b.originCity || '—'}
                          </span>
                          <span className="text-xs text-slate-400">→</span>
                          <span className="text-sm font-bold text-slate-900 dark:text-white truncate">
                            {b.destinationPort || b.destinationCity || '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                            <Calendar size={11} /> {b.date ? formatDate(b.date) : '—'}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                            <Package size={11} /> {b.packages || '—'} pkgs
                          </span>
                          <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                            <Weight size={11} /> {b.weight || '—'} kg
                          </span>
                        </div>
                      </div>

                      {/* Status removed as requested */}

                      {/* Expand chevron */}
                      <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 sm:px-6 pb-5 pt-1 border-t border-slate-100 dark:border-slate-700/50">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 mt-3">
                          {/* Type */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Type</p>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                              {isAir ? <Plane size={13} className="text-blue-500" /> : <Ship size={13} className="text-cyan-500" />}
                              {b.type || '—'}
                            </p>
                          </div>
                          {/* Shipment Type (Sea only) */}
                          {b.shipmentType && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Shipment Type</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.shipmentType}</p>
                            </div>
                          )}
                          {/* Date */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Planning Date</p>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.date ? formatDate(b.date) : '—'}</p>
                          </div>
                          {/* Origin Port (Export) */}
                          {b.originPort && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Origin Port</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.originPort}</p>
                            </div>
                          )}
                          {/* Destination Port (Import) */}
                          {b.destinationPort && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Destination Port</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.destinationPort}</p>
                            </div>
                          )}
                          {/* Containers (Sea) */}
                          {b.containers && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Containers</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.containers}</p>
                            </div>
                          )}
                          {/* Container Type (Sea) */}
                          {b.containerType && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Container Type</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.containerType}</p>
                            </div>
                          )}
                          {/* Destination Country (Export) */}
                          {b.destinationCountry && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Dest. Country</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.destinationCountry}</p>
                            </div>
                          )}
                          {/* Destination City (Export) */}
                          {b.destinationCity && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Dest. City</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.destinationCity}</p>
                            </div>
                          )}
                          {/* Origin Country (Import) */}
                          {b.originCountry && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Origin Country</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.originCountry}</p>
                            </div>
                          )}
                          {/* Origin City (Import) */}
                          {b.originCity && (
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Origin City</p>
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.originCity}</p>
                            </div>
                          )}
                          {/* Packages */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Total Packages</p>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.packages || '—'}</p>
                          </div>
                          {/* Weight */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Gr. Weight</p>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.weight ? `${b.weight} kg` : '—'}</p>
                          </div>
                          {/* Volume / CBM */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                              {b.type && b.type.includes('Air') ? 'Volume Weight' : 'CBM Value'}
                            </p>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                              {b.volume ? `${b.volume} ${b.type && b.type.includes('Air') ? 'kg' : 'CBM'}` : '—'}
                            </p>
                          </div>
                          {/* Commodity */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Commodity</p>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.commodity || '—'}</p>
                          </div>
                          {/* Incoterm */}
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Incoterm</p>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{b.incoterm || '—'}</p>
                          </div>
                        </div>

                        {/* Dimensions */}
                        {b.dimensions && Array.isArray(b.dimensions) && b.dimensions.length > 0 && (
                          <div className="mt-4">
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Dimensions</p>
                            <div className="bg-slate-50 dark:bg-slate-900/30 rounded-xl p-3 overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-400 dark:text-slate-500">
                                    <th className="text-left font-bold pb-1.5 pr-4">L ({b.type && b.type.includes('Air') ? 'mm' : 'cm'})</th>
                                    <th className="text-left font-bold pb-1.5 pr-4">W ({b.type && b.type.includes('Air') ? 'mm' : 'cm'})</th>
                                    <th className="text-left font-bold pb-1.5 pr-4">H ({b.type && b.type.includes('Air') ? 'mm' : 'cm'})</th>
                                    <th className="text-left font-bold pb-1.5">Boxes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(typeof b.dimensions === 'string' ? JSON.parse(b.dimensions) : b.dimensions).map((dim, i) => (
                                    <tr key={i} className="text-slate-700 dark:text-slate-300">
                                      <td className="py-1 pr-4 font-medium">{dim.length}</td>
                                      <td className="py-1 pr-4 font-medium">{dim.width}</td>
                                      <td className="py-1 pr-4 font-medium">{dim.height}</td>
                                      <td className="py-1 font-medium">{dim.boxes || '1'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Services */}
                        {b.services && (
                          <div className="mt-4">
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Services</p>
                            <div className="flex flex-wrap gap-1.5">
                              {(Array.isArray(b.services) ? b.services : typeof b.services === 'string' ? JSON.parse(b.services) : []).map((s, i) => (
                                <span key={i} className="px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium border border-blue-100 dark:border-blue-800">
                                  {s}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Additional Note */}
                        {b.additionalNote && (
                          <div className="mt-4">
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Additional Note</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/30 rounded-xl px-3 py-2">{b.additionalNote}</p>
                          </div>
                        )}

                        {/* Inv/PL file */}
                        {b.invPl && (
                          <div className="mt-4">
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Invoice / Packing List</p>
                            <a
                              href={b.invPl.startsWith('http') ? b.invPl : getCloudFrontUrl(b.invPl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-xs font-medium border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition cursor-pointer"
                            >
                              <FileText size={13} />
                              {typeof b.invPl === 'string' ? b.invPl.split('/').pop() : 'Uploaded'}
                              <Eye size={13} className="ml-1" />
                            </a>
                          </div>
                        )}

                        {/* Created At */}
                        {b.createdAt && (
                          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/50">
                            <p className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-1">
                              <Clock size={10} /> Created: {new Date(b.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        )}

                        {/* Edit / Delete Actions */}
                        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/50 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleEdit(b); }}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition"
                          >
                            <Pencil size={13} />
                            Edit Booking
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDeletingBookingId(b.id); setDeletingBooking(b); setShowDeleteModal(true); }}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-bold border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40 transition"
                          >
                            <Trash2 size={13} />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>

      {/* ─── Delete Confirmation Modal ─── */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!deleting) { setShowDeleteModal(false); setDeletingBookingId(null); } }} />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
                <AlertTriangle size={24} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Delete Booking</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Are you sure you want to delete this booking? This action cannot be undone.
              </p>
              <div className="flex items-center gap-3 w-full">
                <button
                  type="button"
                  onClick={() => { setShowDeleteModal(false); setDeletingBookingId(null); }}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-500/20 transition text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={15} />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </UserShell>
  );
};

export default NewBooking;